// providers/index.ts
import * as Api from '@ViewModels';
import { clioConfig } from './clio';
import { defaultProviderConfig } from './defaults';
import { eclipseConfig } from './eclipse';
import { housecallProConfig } from './housecallPro';
import { microsoftDynamics365Config } from './microsoftDynamics365';
import { myCaseConfig } from './myCase';
import { practicePantherConfig } from './practicePanther';
import { quickBooksOnlineConfig } from './quickBooksOnline';
import { redtailConfig } from './redtail';
import { salesforceConfig } from './salesforce';
import { serviceTitanConfig } from './serviceTitan';
import type { ProviderConfig } from './types';
import { wealthboxConfig } from './wealthbox';

export { ATTORNEY_TAG_OPTIONS, CORE_TAGS, ConfigPath, SettingsKey } from './constants';
export { defaultProviderConfig } from './defaults';
export {
	complementSelect,
	lockableSelect,
	number,
	provider,
	providerTag,
	providerTags,
	searchableSelect,
	select,
	toggle,
} from './helpers';
// Re-export types for convenience
export * from './types';

/**
 * Registry mapping IntegrationProvider enum to provider configs.
 * Add new providers here as they are migrated.
 */
const providerRegistry: Partial<Record<Api.IntegrationProvider, ProviderConfig>> = {
	[Api.IntegrationProvider.Salesforce]: salesforceConfig,
	[Api.IntegrationProvider.ServiceTitan]: serviceTitanConfig,
	[Api.IntegrationProvider.MyCase]: myCaseConfig,
	[Api.IntegrationProvider.PracticePanther]: practicePantherConfig,
	[Api.IntegrationProvider.QuickBooksOnline]: quickBooksOnlineConfig,
	[Api.IntegrationProvider.MicrosoftDynamics365Sales]: microsoftDynamics365Config,
	[Api.IntegrationProvider.Wealthbox]: wealthboxConfig,
	[Api.IntegrationProvider.Clio]: clioConfig,
	[Api.IntegrationProvider.Eclipse]: eclipseConfig,
	[Api.IntegrationProvider.HousecallPro]: housecallProConfig,
	[Api.IntegrationProvider.Redtail]: redtailConfig,
};

/**
 * Get the configuration for a specific provider.
 * Returns default config if provider is not in the registry.
 */
export function getProviderConfig(provider: Api.IntegrationProvider): ProviderConfig {
	return providerRegistry[provider] ?? defaultProviderConfig;
}

/**
 * Check if a provider has a specific configuration registered.
 * Useful for conditional logic where the default fallback behavior of
 * `getProviderConfig` is not appropriate (e.g., feature gating).
 */
export function hasProviderConfig(provider: Api.IntegrationProvider): boolean {
	return provider in providerRegistry;
}
